import type { DesignItem } from '@core/types';
import { isLayerVisible } from '@core/imagePipeline';
import { getPreflightRules, getPrintMethodPacks, type VeyopPreflightContext, type VeyopPreflightRule } from '@core/extensionRegistry';
import type { ViewName } from '@designer/stores/designStore';

export type PreflightSeverity = 'warning' | 'error';
export type PreflightStatus = 'idle' | 'pass' | 'warning' | 'error';
export type PreflightMode = 'placed' | 'full_bleed' | 'wrap_die_cut';
export type PreflightEnforcement = 'off' | 'warn' | 'block';

export interface PreflightPoint { x: number; y: number }
export interface PreflightZone {
  id: string;
  label: string;
  type: 'no_print' | 'seam' | 'camera' | 'handle';
  rect?: { x: number; y: number; width: number; height: number };
  polygon?: PreflightPoint[];
  severity?: PreflightSeverity;
}

export interface PreflightViewProfile {
  mode?: PreflightMode;
  physicalWidthCm?: number;
  physicalHeightCm?: number;
  targetDpi?: number;
  minDpi?: number;
  safetyMm?: number;
  bleedMm?: number;
  /** Minimum physical text size in points; 0 disables the check. */
  minTextPt?: number;
  zones?: PreflightZone[];
}

export interface PreflightProfileV2 extends PreflightViewProfile {
  version: 2;
  id: string;
  mode: PreflightMode;
  enforcement: PreflightEnforcement;
  views?: Partial<Record<ViewName, PreflightViewProfile>>;
}

export type PreflightIssueCode =
  | 'empty_design'
  | 'low_dpi'
  | 'outside_print_bounds'
  | 'outside_safe_area'
  | 'insufficient_bleed'
  | 'exclusion_zone_collision'
  | 'min_text_size'
  | 'low_contrast'
  | 'semi_transparent'
  | 'text_overflow'
  | string;

/**
 * One-tap repair for an issue: a patch for `design.updateItem`. `label` is a stable key the
 * status panel translates (built-in fixes) or an already-translated addon label.
 */
export interface PreflightFix {
  label: string;
  patch: Record<string, unknown>;
  /** Recolored SVGs must be re-rendered after their `svgColors` change. */
  rehydrateSvg?: boolean;
}

export interface PreflightIssue {
  code: PreflightIssueCode;
  severity: PreflightSeverity;
  view: ViewName;
  itemId: string;
  message: string;
  actualDpi?: number;
  zoneId?: string;
  ruleId?: string;
  fix?: PreflightFix;
}

export interface PreflightReportV2 {
  version: 2;
  profileId: string;
  mode: PreflightMode;
  printCode: string;
  outputDpi: number;
  safetyMm: number;
  bleedMm: number;
  enforcement: PreflightEnforcement;
  issues: PreflightIssue[];
  status: PreflightStatus;
  generatedAt: string;
}

export const PREFLIGHT_PRESETS: Record<PreflightMode, PreflightProfileV2> = {
  placed: { version: 2, id: 'placed', mode: 'placed', targetDpi: 300, minDpi: 150, safetyMm: 3, bleedMm: 0, minTextPt: 6, enforcement: 'warn' },
  full_bleed: { version: 2, id: 'full_bleed', mode: 'full_bleed', targetDpi: 300, minDpi: 150, safetyMm: 3, bleedMm: 3, minTextPt: 6, enforcement: 'warn' },
  wrap_die_cut: { version: 2, id: 'wrap_die_cut', mode: 'wrap_die_cut', targetDpi: 300, minDpi: 150, safetyMm: 3, bleedMm: 3, minTextPt: 6, enforcement: 'warn' },
};

type PreflightServerDefaults = {
  enforcement: PreflightEnforcement;
  targetDpi: number;
  minDpi: number;
  safetyMm: number;
  bleedMm: number;
  minTextPt: number;
};

/** Site-wide defaults configured in the admin Preflight settings page, hydrated once the designer config loads. */
let serverDefaults: PreflightServerDefaults | null = null;

export function setPreflightServerDefaults(raw: unknown): void {
  if (!raw || typeof raw !== 'object') return;
  const input = raw as Partial<PreflightServerDefaults>;
  serverDefaults = {
    enforcement: input.enforcement === 'off' || input.enforcement === 'block' ? input.enforcement : 'warn',
    targetDpi: positive(input.targetDpi, 300),
    minDpi: positive(input.minDpi, 150),
    safetyMm: nonNegative(input.safetyMm, 3),
    bleedMm: nonNegative(input.bleedMm, 3),
    minTextPt: nonNegative(input.minTextPt, 6),
  };
}

/** Preset for a mode, with site-wide admin defaults layered in (placed mode keeps its zero-bleed anchor). */
function presetForMode(mode: PreflightMode): PreflightProfileV2 {
  const base = PREFLIGHT_PRESETS[mode];
  if (!serverDefaults) return base;
  return {
    ...base,
    enforcement: serverDefaults.enforcement,
    targetDpi: serverDefaults.targetDpi,
    minDpi: serverDefaults.minDpi,
    safetyMm: serverDefaults.safetyMm,
    bleedMm: mode === 'placed' ? base.bleedMm : serverDefaults.bleedMm,
    minTextPt: serverDefaults.minTextPt,
  };
}

export function normalizePreflightProfile(raw: unknown): PreflightProfileV2 {
  const input = raw && typeof raw === 'object' ? raw as Partial<PreflightProfileV2> : {};
  const mode: PreflightMode = input.mode === 'full_bleed' || input.mode === 'wrap_die_cut' ? input.mode : 'placed';
  const base = presetForMode(mode);
  return {
    ...base,
    ...input,
    version: 2,
    id: String(input.id || mode),
    mode,
    enforcement: input.enforcement === 'off' || input.enforcement === 'block' ? input.enforcement : base.enforcement,
    targetDpi: positive(input.targetDpi, base.targetDpi || 300),
    minDpi: positive(input.minDpi, base.minDpi || 150),
    safetyMm: nonNegative(input.safetyMm, base.safetyMm || 0),
    bleedMm: nonNegative(input.bleedMm, base.bleedMm || 0),
    minTextPt: nonNegative(input.minTextPt, base.minTextPt ?? 6),
    views: input.views && typeof input.views === 'object' ? input.views : {},
  };
}

/** The report options' profile with the global output DPI / safety / bleed fallbacks applied. */
export function resolvePreflightProfile(options: Pick<PreflightReportOptions, 'profile' | 'outputDpi' | 'safetyMm' | 'bleedMm'>): PreflightProfileV2 {
  return normalizePreflightProfile({
    ...(options.profile ?? {}),
    // Global output DPI is only a fallback; a per-product target DPI wins.
    ...(options.outputDpi && !(positive(options.profile?.targetDpi, 0) > 0) ? { targetDpi: options.outputDpi } : {}),
    ...(options.safetyMm != null ? { safetyMm: options.safetyMm } : {}),
    ...(options.bleedMm != null ? { bleedMm: options.bleedMm } : {}),
  });
}

export interface ViewPrintContext {
  viewProfile: PreflightProfileV2 & PreflightViewProfile;
  viewMode: PreflightMode;
  /** Printable area of the view in canvas px. */
  boundsWidth: number;
  boundsHeight: number;
  physicalWidthCm: number;
  physicalHeightCm: number;
  targetDpi: number;
  minDpi: number;
  safetyMm: number;
  bleedMm: number;
  minTextPt: number;
}

/** Print geometry and thresholds of one view: per-view profile overrides win over the product profile. */
export function resolveViewPrintContext(
  view: ViewName,
  profile: PreflightProfileV2,
  options: Pick<PreflightReportOptions, 'stageWidth' | 'stageHeight' | 'areaSizes' | 'physicalSizesCm'>,
): ViewPrintContext {
  const stageWidth = positive(options.stageWidth, 500);
  const stageHeight = positive(options.stageHeight, 500);
  const viewProfile = { ...profile, ...(profile.views?.[view] ?? {}) };
  const viewMode = viewProfile.mode === 'full_bleed' || viewProfile.mode === 'wrap_die_cut' ? viewProfile.mode : viewProfile.mode === 'placed' ? 'placed' : profile.mode;
  const boundsWidth = positive(options.areaSizes?.[view]?.width, stageWidth);
  const boundsHeight = positive(options.areaSizes?.[view]?.height, stageHeight);
  const fallbackCmPerPx = 2.54 / positive(viewProfile.targetDpi, 300);
  return {
    viewProfile,
    viewMode,
    boundsWidth,
    boundsHeight,
    physicalWidthCm: positive(viewProfile.physicalWidthCm ?? options.physicalSizesCm?.[view]?.width, boundsWidth * fallbackCmPerPx),
    physicalHeightCm: positive(viewProfile.physicalHeightCm ?? options.physicalSizesCm?.[view]?.height, boundsHeight * fallbackCmPerPx),
    targetDpi: positive(viewProfile.targetDpi, 300),
    minDpi: positive(viewProfile.minDpi, 150),
    safetyMm: nonNegative(viewProfile.safetyMm, 3),
    bleedMm: nonNegative(viewProfile.bleedMm, profile.mode === 'placed' ? 0 : 3),
    minTextPt: nonNegative(viewProfile.minTextPt, 6),
  };
}

/**
 * Effective print resolution of an image item: the source pixels left after the crop over the
 * printed size in inches (the worse axis). Null when the source or printed size is unknown.
 */
export function imagePrintDpi(
  item: DesignItem,
  ctx: Pick<ViewPrintContext, 'boundsWidth' | 'boundsHeight' | 'physicalWidthCm' | 'physicalHeightCm'>,
): number | null {
  const sourceWidth = positive(item.sourcePixelWidth, 0);
  const sourceHeight = positive(item.sourcePixelHeight, 0);
  const crop = (item.imageTransform as { crop?: { width?: number; height?: number } } | undefined)?.crop;
  const usableWidth = sourceWidth * fraction(crop?.width, 1);
  const usableHeight = sourceHeight * fraction(crop?.height, 1);
  const printedWidthIn = ((positive(item.width, 0) / ctx.boundsWidth) * ctx.physicalWidthCm) / 2.54;
  const printedHeightIn = ((positive(item.height, 0) / ctx.boundsHeight) * ctx.physicalHeightCm) / 2.54;
  if (!(usableWidth && usableHeight && printedWidthIn && printedHeightIn)) return null;
  return Math.min(usableWidth / printedWidthIn, usableHeight / printedHeightIn);
}

export function buildPreflightReport(
  items: Record<ViewName, DesignItem[]>,
  options: {
    stageWidth: number;
    stageHeight: number;
    outputDpi?: number;
    safetyMm?: number;
    bleedMm?: number;
    areaSizes?: Partial<Record<ViewName, { width: number; height: number }>>;
    physicalSizesCm?: Partial<Record<ViewName, { width: number; height: number }>>;
    profile?: Partial<PreflightProfileV2> | null;
    activePrintCode?: string;
    activePrintMethodPack?: string;
    embroideryProfile?: unknown;
    /** Selected garment colors as hex strings (with or without '#'). */
    garmentColors?: string[];
    /** Textured garments skip the low-contrast check (no single reference color). */
    garmentHasTexture?: boolean;
    /**
     * Size of what a layer really paints (`Veyop.itemBounds.measureContent`): text wider than
     * its box, addon layouts. Bounds checks use the larger of box and content, and text whose
     * content outgrows its box is reported as `text_overflow`.
     */
    measureContent?: (item: DesignItem) => { width: number; height: number } | null;
  },
): PreflightReportV2 {
  const profile = resolvePreflightProfile(options);
  const issues: PreflightIssue[] = [];
  let visibleCount = 0;
  const contexts = new Map<ViewName, VeyopPreflightContext>();
  const garmentRgbs = options.garmentHasTexture
    ? []
    : (options.garmentColors ?? []).map(parseHexColor).filter((rgb): rgb is [number, number, number] => rgb !== null);
  const isEmbroideryActive = String(options.activePrintMethodPack || '') === 'embroidery-pack'
    || /embroid/i.test(String(options.activePrintCode || ''));

  for (const [view, layers] of Object.entries(items) as [ViewName, DesignItem[]][]) {
    const printContext = resolveViewPrintContext(view, profile, options);
    const {
      viewProfile, viewMode, boundsWidth, boundsHeight, physicalWidthCm, physicalHeightCm,
      targetDpi, minDpi, safetyMm, bleedMm, minTextPt,
    } = printContext;
    contexts.set(view, {
      printCode: String(options.activePrintCode || ''),
      methodPackId: String(options.activePrintMethodPack || ''),
      view,
      physicalWidthCm,
      physicalHeightCm,
      areaWidthPx: boundsWidth,
      areaHeightPx: boundsHeight,
      embroideryProfile: options.embroideryProfile,
    });
    const safeInsetX = (((safetyMm + bleedMm) / 10) / physicalWidthCm) * boundsWidth;
    const safeInsetY = (((safetyMm + bleedMm) / 10) / physicalHeightCm) * boundsHeight;
    const visible = layers.filter(isLayerVisible);
    visibleCount += visible.length;
    let union: { minX: number; minY: number; maxX: number; maxY: number } | null = null;

    for (const item of visible) {
      const content = options.measureContent?.(item) ?? null;
      // Bounds checks see what is painted: the wider of box and content (text: width only, see below).
      const painted = content ? withContentSize(item, item.type === 'text' ? { width: content.width, height: 0 } : content) : item;
      const polygon = rotatedItemPolygon(painted);
      const bounds = polygonBounds(polygon);
      union = unionBounds(union, bounds);

      const overflows = bounds.minX < 0 || bounds.minY < 0 || bounds.maxX > boundsWidth || bounds.maxY > boundsHeight;
      const entirelyOutside = bounds.maxX <= 0 || bounds.minX >= boundsWidth || bounds.maxY <= 0 || bounds.minY >= boundsHeight;
      // In bleed modes an item spanning the whole canvas is a background supplying bleed,
      // so overfilling is expected and exempt from the safe-area warning.
      const coversCanvas = bounds.minX <= 0 && bounds.minY <= 0 && bounds.maxX >= boundsWidth && bounds.maxY >= boundsHeight;
      const crossesSafeArea = bounds.minX < safeInsetX || bounds.minY < safeInsetY || bounds.maxX > boundsWidth - safeInsetX || bounds.maxY > boundsHeight - safeInsetY;
      // Fitting into the safe rect clears both the bounds error and the safe-area warning.
      const safeRect = {
        x: safeInsetX,
        y: safeInsetY,
        width: Math.max(1, boundsWidth - safeInsetX * 2),
        height: Math.max(1, boundsHeight - safeInsetY * 2),
      };
      if (viewMode === 'placed' ? overflows : entirelyOutside) {
        issues.push({
          code: 'outside_print_bounds', severity: 'error', view, itemId: item.id,
          message: viewMode === 'placed'
            ? `${view}: an item extends outside the printable bounds.`
            : `${view}: an item is entirely outside the printable canvas.`,
          fix: { label: 'fit_print_area', patch: fitInsideRect(item, bounds, safeRect) },
        });
      } else if (crossesSafeArea && !(viewMode !== 'placed' && coversCanvas)) {
        issues.push({
          code: 'outside_safe_area', severity: 'warning', view, itemId: item.id,
          message: `${view}: an item crosses the ${safetyMm} mm safety area.`,
          fix: { label: 'fit_safe_area', patch: fitInsideRect(item, bounds, safeRect) },
        });
      }

      // One-line text that is wider than its box wraps and loses its last words. Height is left
      // alone: the measurer pads vertically more than saved boxes do, and a line cannot wrap down.
      if (item.type === 'text' && content) {
        const width = positive(item.width, 0);
        // 2 px of slack: measuring rounds up and anti-aliasing paints a hair outside the glyphs.
        if (content.width > width + 2) {
          const grown = withContentSize(item, { width: content.width, height: 0 });
          issues.push({
            code: 'text_overflow', severity: 'warning', view, itemId: item.id,
            message: `${view}: the text is wider than its box and is cut off.`,
            fix: { label: 'fit_text_box', patch: { left: grown.left, width: grown.width } },
          });
        }
      }

      if (item.type === 'image') {
        const actualDpi = imagePrintDpi(item, printContext);
        if (actualDpi != null) {
          if (actualDpi < targetDpi) {
            issues.push({
              code: 'low_dpi',
              severity: actualDpi < minDpi ? 'error' : 'warning',
              view,
              itemId: item.id,
              actualDpi: Math.round(actualDpi),
              message: `${view}: image resolution is ${Math.round(actualDpi)} DPI; target is ${targetDpi} DPI.`,
              fix: { label: 'shrink_sharp', patch: scaleAroundCenter(item, actualDpi / targetDpi) },
            });
          }
        }
      }

      if (item.type === 'text' && minTextPt > 0) {
        const fontPx = positive(item.fontSize, 0);
        if (fontPx > 0) {
          const fontPt = (((fontPx / boundsWidth) * physicalWidthCm) / 2.54) * 72;
          if (fontPt < minTextPt) {
            issues.push({
              code: 'min_text_size', severity: 'warning', view, itemId: item.id,
              message: `${view}: text prints at ${fontPt.toFixed(1)} pt; minimum recommended is ${minTextPt} pt.`,
              // The store re-measures the text box when fontSize changes.
              fix: { label: 'bigger_text', patch: { fontSize: Math.ceil(fontPx * (minTextPt / fontPt)) } },
            });
          }
        }
      }

      if (garmentRgbs.length) {
        const fills = (item.type === 'text'
          ? [item.color]
          : item.type === 'svg'
            ? Object.values(item.svgColors ?? {})
            : []
        ).map(parseHexColor).filter((rgb): rgb is [number, number, number] => rgb !== null);
        // Only flag when every fill is near every garment color — multi-color garments
        // effectively opt out, keeping false positives near zero.
        if (fills.length && fills.every((fill) => garmentRgbs.every((garment) => rgbDistance(fill, garment) < 48))) {
          const contrast = contrastingInk(garmentRgbs);
          issues.push({
            code: 'low_contrast', severity: 'warning', view, itemId: item.id,
            message: `${view}: artwork color is nearly identical to the selected product color and may be invisible.`,
            fix: item.type === 'text'
              ? { label: 'contrast_color', patch: { color: contrast } }
              : {
                label: 'contrast_color',
                patch: { svgColors: Object.fromEntries(Object.keys(item.svgColors ?? {}).map((original) => [original, contrast])) },
                rehydrateSvg: true,
              },
          });
        }
      }

      const opacity = Number(item.opacity);
      if (Number.isFinite(opacity) && opacity < 0.99 && !isEmbroideryActive) {
        issues.push({
          code: 'semi_transparent', severity: 'warning', view, itemId: item.id,
          message: `${view}: an item is semi-transparent; transparency may print as halftone dots.`,
          fix: { label: 'opaque', patch: { opacity: 1 } },
        });
      }

      for (const zone of viewProfile.zones ?? []) {
        const zonePolygon = normalizedZonePolygon(zone, boundsWidth, boundsHeight);
        if (zonePolygon.length >= 3 && polygonsIntersect(polygon, zonePolygon)) {
          issues.push({
            code: 'exclusion_zone_collision', severity: zone.severity === 'warning' ? 'warning' : 'error', view, itemId: item.id, zoneId: zone.id,
            message: `${view}: an item overlaps ${zone.label || zone.type}.`,
          });
        }
      }
    }

    if (visible.length && viewMode !== 'placed' && union) {
      const tolerance = 0.5;
      if (union.minX > tolerance || union.minY > tolerance || union.maxX < boundsWidth - tolerance || union.maxY < boundsHeight - tolerance) {
        issues.push({
          code: 'insufficient_bleed', severity: 'warning', view, itemId: '',
          message: `${view}: artwork does not cover the full ${bleedMm} mm bleed canvas.`,
        });
      }
    }
  }

  if (visibleCount === 0) {
    issues.push({ code: 'empty_design', severity: 'warning', view: 'front', itemId: '', message: 'Add artwork to run print checks.' });
  }

  const activePrintCode = String(options.activePrintCode || '').trim().toLowerCase();
  const activePrintMethodPack = String(options.activePrintMethodPack || '').trim();
  for (const pack of getPrintMethodPacks()) {
    const packPrintCode = String(pack.printCode || '').trim().toLowerCase();
    const legacyCodes = (pack.legacyPrintCodes ?? []).map((code) => String(code).trim().toLowerCase());
    const packIsActive = activePrintMethodPack
      ? activePrintMethodPack === pack.id
      : Boolean(packPrintCode && packPrintCode === activePrintCode) || legacyCodes.includes(activePrintCode);
    if (!packIsActive) {
      if (pack.enforcePrintCode && pack.matchesItem) {
        for (const [view, layers] of Object.entries(items) as [ViewName, DesignItem[]][]) {
          for (const item of layers) {
            if (!isLayerVisible(item) || !pack.matchesItem(item)) continue;
            issues.push({
              code: 'print_method_mismatch',
              severity: 'error',
              view,
              itemId: item.id,
              message: `${view}: ${pack.label} artwork requires a printing type linked to ${pack.label}.`,
            });
          }
        }
      }
      continue;
    }
    for (const rule of pack.preflightRules ?? []) {
      for (const [view, layers] of Object.entries(items) as [ViewName, DesignItem[]][]) {
        for (const item of layers) {
          if (!isLayerVisible(item) || (pack.matchesItem && !pack.matchesItem(item)) || (rule.matches && !rule.matches(item, contexts.get(view)))) continue;
          issues.push({ code: rule.id, ruleId: rule.id, severity: rule.severity, view, itemId: item.id, message: `${view}: ${rule.message}`, fix: ruleFix(rule, item, contexts.get(view)) });
        }
      }
    }
  }

  for (const rule of getPreflightRules()) {
    for (const [view, layers] of Object.entries(items) as [ViewName, DesignItem[]][]) {
      for (const item of layers) {
        if (!isLayerVisible(item) || (rule.matches && !rule.matches(item, contexts.get(view)))) continue;
        issues.push({ code: rule.id, ruleId: rule.id, severity: rule.severity, view, itemId: item.id, message: `${view}: ${rule.message}`, fix: ruleFix(rule, item, contexts.get(view)) });
      }
    }
  }

  const status: PreflightStatus = visibleCount === 0
    ? 'idle'
    : issues.some((issue) => issue.severity === 'error')
      ? 'error'
      : issues.length ? 'warning' : 'pass';
  return {
    version: 2,
    profileId: profile.id,
    mode: profile.mode,
    printCode: activePrintCode,
    outputDpi: positive(profile.targetDpi, 300),
    safetyMm: nonNegative(profile.safetyMm, 3),
    bleedMm: nonNegative(profile.bleedMm, 0),
    enforcement: profile.enforcement,
    issues,
    status,
    generatedAt: new Date().toISOString(),
  };
}

function ruleFix(rule: VeyopPreflightRule, item: DesignItem, context: VeyopPreflightContext | undefined): PreflightFix | undefined {
  if (!rule.fix) return undefined;
  try {
    const patch = rule.fix.apply(item, context);
    return patch ? { label: rule.fix.label, patch } : undefined;
  } catch {
    return undefined;
  }
}

/** Uniform scale about the item centre; text scales its font so the box re-measures to match. */
function scaleAroundCenter(item: DesignItem, scale: number): Record<string, unknown> {
  const s = Math.max(0.05, Math.min(1, scale));
  const width = positive(item.width, 1);
  const height = positive(item.height, 1);
  const cx = (Number(item.left) || 0) + width / 2;
  const cy = (Number(item.top) || 0) + height / 2;
  const nextW = width * s;
  const nextH = height * s;
  const patch: Record<string, unknown> = { width: nextW, height: nextH, left: cx - nextW / 2, top: cy - nextH / 2 };
  if (item.type === 'text' && positive(item.fontSize, 0)) patch.fontSize = Math.max(6, Math.floor(Number(item.fontSize) * s));
  return patch;
}

/** Shrink (never grow) and move so the item's rotated bounding box sits inside `rect`. */
function fitInsideRect(
  item: DesignItem,
  bounds: ReturnType<typeof polygonBounds>,
  rect: { x: number; y: number; width: number; height: number },
): Record<string, unknown> {
  const bw = Math.max(1e-6, bounds.maxX - bounds.minX);
  const bh = Math.max(1e-6, bounds.maxY - bounds.minY);
  const s = Math.min(1, rect.width / bw, rect.height / bh) * 0.999;
  const halfW = (bw * s) / 2;
  const halfH = (bh * s) / 2;
  const cx = Math.min(Math.max((bounds.minX + bounds.maxX) / 2, rect.x + halfW), rect.x + rect.width - halfW);
  const cy = Math.min(Math.max((bounds.minY + bounds.maxY) / 2, rect.y + halfH), rect.y + rect.height - halfH);
  const width = positive(item.width, 1) * s;
  const height = positive(item.height, 1) * s;
  const patch: Record<string, unknown> = { width, height, left: cx - width / 2, top: cy - height / 2 };
  if (item.type === 'text' && s < 1 && positive(item.fontSize, 0)) patch.fontSize = Math.max(6, Math.floor(Number(item.fontSize) * s));
  return patch;
}

/** Black or white, whichever stands out more against the (average) garment color. */
function contrastingInk(garments: [number, number, number][]): string {
  const avg = garments.reduce((sum, rgb) => sum + (0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2]), 0) / garments.length;
  return avg < 140 ? '#ffffff' : '#111111';
}

/** The item's box grown (around its centre) to at least its painted content. */
function withContentSize(item: DesignItem, content: { width: number; height: number }): DesignItem {
  const width = positive(item.width, 0);
  const height = positive(item.height, 0);
  const nextW = Math.max(width, positive(content.width, 0));
  const nextH = Math.max(height, positive(content.height, 0));
  if (nextW === width && nextH === height) return item;
  return {
    ...item,
    left: Math.round(((Number(item.left) || 0) - (nextW - width) / 2) * 100) / 100,
    top: Math.round(((Number(item.top) || 0) - (nextH - height) / 2) * 100) / 100,
    width: Math.ceil(nextW),
    height: Math.ceil(nextH),
  };
}

function rotatedItemPolygon(item: DesignItem): PreflightPoint[] {
  const left = Number(item.left) || 0;
  const top = Number(item.top) || 0;
  const width = positive(item.width, 0);
  const height = positive(item.height, 0);
  const cx = left + width / 2;
  const cy = top + height / 2;
  const radians = ((Number(item.angle) || 0) * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return [[left, top], [left + width, top], [left + width, top + height], [left, top + height]].map(([x, y]) => ({
    x: cx + (x - cx) * cos - (y - cy) * sin,
    y: cy + (x - cx) * sin + (y - cy) * cos,
  }));
}

function polygonBounds(points: PreflightPoint[]) {
  return { minX: Math.min(...points.map((p) => p.x)), minY: Math.min(...points.map((p) => p.y)), maxX: Math.max(...points.map((p) => p.x)), maxY: Math.max(...points.map((p) => p.y)) };
}

function unionBounds(a: ReturnType<typeof polygonBounds> | null, b: ReturnType<typeof polygonBounds>) {
  return a ? { minX: Math.min(a.minX, b.minX), minY: Math.min(a.minY, b.minY), maxX: Math.max(a.maxX, b.maxX), maxY: Math.max(a.maxY, b.maxY) } : b;
}

function normalizedZonePolygon(zone: PreflightZone, width: number, height: number): PreflightPoint[] {
  if (zone.polygon?.length) return zone.polygon.map((point) => ({ x: point.x * width, y: point.y * height }));
  const r = zone.rect;
  if (!r) return [];
  return [
    { x: r.x * width, y: r.y * height },
    { x: (r.x + r.width) * width, y: r.y * height },
    { x: (r.x + r.width) * width, y: (r.y + r.height) * height },
    { x: r.x * width, y: (r.y + r.height) * height },
  ];
}

function polygonsIntersect(a: PreflightPoint[], b: PreflightPoint[]): boolean {
  if (a.some((point) => pointInPolygon(point, b)) || b.some((point) => pointInPolygon(point, a))) return true;
  for (let ai = 0; ai < a.length; ai++) for (let bi = 0; bi < b.length; bi++) {
    if (segmentsIntersect(a[ai], a[(ai + 1) % a.length], b[bi], b[(bi + 1) % b.length])) return true;
  }
  return false;
}

function pointInPolygon(point: PreflightPoint, polygon: PreflightPoint[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i]; const b = polygon[j];
    if ((a.y > point.y) !== (b.y > point.y) && point.x < ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y || 1e-9) + a.x) inside = !inside;
  }
  return inside;
}

function segmentsIntersect(a: PreflightPoint, b: PreflightPoint, c: PreflightPoint, d: PreflightPoint): boolean {
  const cross = (p: PreflightPoint, q: PreflightPoint, r: PreflightPoint) => (q.x - p.x) * (r.y - p.y) - (q.y - p.y) * (r.x - p.x);
  return cross(a, b, c) * cross(a, b, d) <= 0 && cross(c, d, a) * cross(c, d, b) <= 0;
}

function parseHexColor(value: unknown): [number, number, number] | null {
  if (typeof value !== 'string') return null;
  const hex = value.trim().replace(/^#/, '');
  if (/^[0-9a-f]{3}$/i.test(hex)) {
    return [parseInt(hex[0] + hex[0], 16), parseInt(hex[1] + hex[1], 16), parseInt(hex[2] + hex[2], 16)];
  }
  if (/^[0-9a-f]{6}$/i.test(hex)) {
    return [parseInt(hex.slice(0, 2), 16), parseInt(hex.slice(2, 4), 16), parseInt(hex.slice(4, 6), 16)];
  }
  return null;
}

function rgbDistance(a: [number, number, number], b: [number, number, number]): number {
  return Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
}

function positive(value: unknown, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}
function nonNegative(value: unknown, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
}
function fraction(value: unknown, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 && parsed <= 1 ? parsed : fallback;
}

export type PreflightReportOptions = Parameters<typeof buildPreflightReport>[1];
